import { useEffect, useState } from 'react';
import { api, type PlatformScores, type ScoreCell, type ScoreSpread } from '../api.ts';
import { message } from '../format.ts';
import { ScoreFigure, ScoreRange, StaffOnlyChip } from './ScoreParts.tsx';
import {
  NOT_YET_ASSESSED,
  isAssessed,
  percentileText,
  rankText,
  spreadValue,
} from './scores.ts';

/**
 * One client's scores, on its Overview, for platform staff only (ADR-0081).
 *
 * Rendered by `TenantDetail` behind `mayLoadScores(me)`, and it is that gate —
 * not anything in here — that keeps a customer's browser from asking. It reads
 * the whole platform payload because there is no per-tenant route, and the
 * platform's own spread is half of what this panel is for: a number with no
 * neighbours invites a reader to decide for themselves whether it is good.
 */

interface Props {
  readonly tenantId: string;
}

export const TenantScores = ({ tenantId }: Props) => {
  const [scores, setScores] = useState<PlatformScores | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .platformScores()
      .then(setScores)
      .catch((cause: unknown) => setError(message(cause)));
  }, []);

  const tenant = scores?.tenants.find((entry) => entry.tenantId === tenantId) ?? null;

  return (
    <div className="card sc-panel">
      <div className="card-head">
        <h2>Scores (staff only)</h2>
        <StaffOnlyChip />
      </div>
      <div className="card-body">
        {scores !== null && <p className="sc-panel-lead">{scores.definition.summary}</p>}
        {error !== null ? (
          <p className="note">Scores could not be loaded: {error}</p>
        ) : scores === null ? (
          <p className="note">Loading scores…</p>
        ) : tenant === null ? (
          <p className="note">
            This client is not in the scored estate — only live tenants are scored.
          </p>
        ) : !isAssessed(tenant) ? (
          <p className="note">{NOT_YET_ASSESSED}. A score appears after the first completed assessment.</p>
        ) : (
          <>
            <p className="note sc-panel-lead">
              Rank <span className="num">{rankText(tenant.position)}</span> overall ·{' '}
              <span className="num">{percentileText(tenant.position)}</span>
            </p>
            <div className="tbl-wrap">
              <table className="tbl sc-table">
                <thead>
                  <tr>
                    <th scope="col">Scope</th>
                    <th scope="col">This client</th>
                    <th scope="col">Platform minimum</th>
                    <th scope="col">Median</th>
                    <th scope="col">Maximum</th>
                    <th scope="col">Where it sits</th>
                  </tr>
                </thead>
                <tbody>
                  <Line name="Overall" cell={tenant.overall} spread={scores.platform.overall} />
                  {scores.frameworks.map((framework) => (
                    <Line
                      key={framework.id}
                      name={framework.name}
                      cell={tenant.frameworks[framework.id]}
                      spread={scores.platform.frameworks[framework.id]}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface LineProps {
  readonly name: string;
  readonly cell: ScoreCell;
  readonly spread: ScoreSpread;
}

const Line = ({ name, cell, spread }: LineProps) => (
  <tr>
    <th scope="row" className="sc-name">
      {name}
    </th>
    <td>
      <ScoreFigure cell={cell} />
    </td>
    <td className="num">{spreadValue(spread.min)}</td>
    <td className="num">{spreadValue(spread.median)}</td>
    <td className="num">{spreadValue(spread.max)}</td>
    <td>
      <ScoreRange spread={spread} at={cell.score} />
    </td>
  </tr>
);
